import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { QairoLogo } from "../components/QairoLogo";
import { formatApiError } from "../lib/formatApiError";
import { API } from "../lib/constants";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <header className="border-b border-black">
          <div className="max-w-7xl mx-auto px-8 py-4">
            <Link to="/"><QairoLogo variant="light" /></Link>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Invalid Reset Link</h1>
            <p className="text-gray-600 mb-6">This password reset link is invalid or has expired.</p>
            <Link to="/forgot-password" className="text-[#0066FF] font-bold hover:underline">Request a new reset link</Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="border-b border-black">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/"><QairoLogo variant="light" /></Link>
          <Link to="/login" className="text-sm font-medium hover:text-[#0066FF]">Back to Login</Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="border border-black p-8 rounded-xl">
            <h1 className="text-2xl font-bold mb-2">Set New Password</h1>
            <p className="text-gray-600 mb-6">Choose a strong password for your account.</p>

            {success ? (
              <div data-testid="reset-success">
                <div className="bg-green-50 border border-green-500 text-green-700 p-4 mb-4 rounded-lg">
                  Your password has been reset successfully.
                </div>
                <Link to="/login" className="block text-center bg-[#0066FF] text-white py-3 font-bold hover:bg-[#0052CC] transition-colors rounded-lg" data-testid="reset-login-link">
                  Login with New Password
                </Link>
              </div>
            ) : (
              <>
                {error && <div className="bg-red-50 border border-red-500 text-red-700 p-3 mb-4 text-sm rounded-lg" data-testid="reset-error">{error}</div>}
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">New Password</label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg"
                      required
                      minLength={6}
                      data-testid="reset-password"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Confirm Password</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg"
                      required
                      minLength={6}
                      data-testid="reset-confirm-password"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#0066FF] text-white py-3 font-bold hover:bg-[#0052CC] transition-colors disabled:opacity-50 rounded-lg"
                    data-testid="reset-submit"
                  >
                    {loading ? "Resetting..." : "Reset Password"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>

      <footer className="py-8 px-8 border-t border-black bg-black text-white">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <QairoLogo variant="dark" />
          <p className="text-sm text-gray-400">&copy; 2026 QAIRO AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
